import { createPortal } from "react-dom";
import { BellRing, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ensurePush, needsIosInstall } from "@/components/PushToggle";

// Shown when a new admin message arrives on a device without alerts, asking for the one required "Allow" tap.
export const AlertPrompt = ({ message, onClose }) => {
  const { t } = useTranslation();
  if (!message) return null;

  const turnOn = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm === "granted" && await ensurePush()) toast.success(t("inbox.pushEnabled"));
    } catch { /* unsupported */ }
    onClose();
  };

  return createPortal(
    <div data-testid="alert-prompt" className="fixed inset-x-3 bottom-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-96 z-[90] bg-[#141418] border border-brand/40 rounded-2xl shadow-2xl shadow-black/60 p-5 animate-in slide-in-from-bottom-4 fade-in">
      <button data-testid="alert-prompt-close" onClick={onClose} aria-label="Close" className="absolute top-3 right-3 text-zinc-500 hover:text-white"><X size={16} /></button>
      <div className="flex gap-3">
        <div className="w-10 h-10 shrink-0 rounded-full bg-brand/20 text-brand grid place-items-center"><BellRing size={19} /></div>
        <div className="min-w-0">
          <p className="text-xs text-zinc-400">{t("inbox.newMessage")}</p>
          <p data-testid="alert-prompt-title" className="font-semibold truncate">{message.title}</p>
          <p className="text-sm text-zinc-300 mt-2">{needsIosInstall() ? t("inbox.pushIos") : t("inbox.promptBody")}</p>
        </div>
      </div>
      {!needsIosInstall() && (
        <div className="flex gap-2 mt-4">
          <button data-testid="alert-prompt-enable" onClick={turnOn}
            className="flex-1 bg-brand hover:bg-red-600 text-white font-bold text-sm py-2.5 rounded-lg transition-colors">{t("inbox.pushEnable")}</button>
          <button data-testid="alert-prompt-later" onClick={onClose}
            className="px-4 text-sm text-zinc-400 hover:text-white rounded-lg bg-white/5 hover:bg-white/10 transition-colors">{t("inbox.notNow")}</button>
        </div>
      )}
    </div>,
    document.body,
  );
};
